import { Ban, Snowflake } from "lucide-react";
import { useState } from "react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { findGradient } from "../constants/cardGradients";
import { useOrgDesign } from "../hooks/useOrgDesign";
import type { CardStylesConfig } from "../types/branding.types";
import type { Card } from "../types/cards-api.types";

import styles from "./CardVisual.module.css";

type CardVisualProps = {
  card: Card;
  viewUrl: string | null;
  isGeneratingUrl: boolean;
  design?: CardStylesConfig | null;
  frozen?: boolean;
  disabled?: boolean;
};

export const CardVisual = ({
  card,
  viewUrl,
  isGeneratingUrl,
  design,
  frozen = false,
  disabled = false,
}: CardVisualProps) => {
  const { t } = useTranslations("cards");
  const orgDesign = useOrgDesign();
  const effectiveDesign = design ?? orgDesign;
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const isLoadingIframe = viewUrl !== null && viewUrl !== loadedUrl;

  const last4 = card.last4 || card.last_4 || "0000";
  const cardNumber = `**** **** **** ${last4}`;
  const holder = card.holder || card.card_holder_name || card.name || "—";

  const customGradient = effectiveDesign?.gradient_id
    ? [
        "radial-gradient(ellipse 80% 60% at 80% 0%, rgba(255,255,255,0.18), transparent 60%)",
        "radial-gradient(ellipse 100% 80% at 0% 100%, rgba(255,255,255,0.10), transparent 60%)",
        findGradient(effectiveDesign.gradient_id).css,
      ].join(", ")
    : null;
  const secondaryLogoUrl = effectiveDesign?.secondary_logo_url ?? null;
  const secondaryLogoScale = effectiveDesign?.secondary_logo_scale ?? 1;
  const secondaryLogoPx = Math.round(40 * secondaryLogoScale);
  // Centre-align with Coloca's watermark so scaling stays balanced.
  const secondaryLogoTranslateY = `${(40 - secondaryLogoPx) / 2}px`;

  // The API sends `expiration_date` as "M/YYYY" (e.g. "3/2032"), which
  // `new Date(...)` cannot parse (it yields Invalid Date → "NaN/aN"). Parse the
  // month/year explicitly; also tolerate "MM/YY" and the separate
  // expiry_month/expiry_year fields.
  const parseExpiry = (): { mm: string; yy: string } | null => {
    const raw = card.expiration_date?.trim();
    let month: number | null = null;
    let year: number | null = null;

    if (raw && /^\d{1,2}\/\d{2,4}$/.test(raw)) {
      const [m, y] = raw.split("/");
      month = parseInt(m, 10);
      year = parseInt(y, 10);
    } else if (card.expiry_month && card.expiry_year) {
      month = parseInt(String(card.expiry_month), 10);
      year = parseInt(String(card.expiry_year), 10);
    }

    if (!month || !year || Number.isNaN(month) || Number.isNaN(year)) return null;

    return { mm: String(month).padStart(2, "0"), yy: String(year).slice(-2) };
  };

  const parsedExpiry = parseExpiry();
  const formattedExpiry = parsedExpiry ? `${parsedExpiry.mm}/${parsedExpiry.yy}` : "**/**";

  return (
    <div className="mx-auto w-full">
      <div
        className={cn(
          "w-full aspect-[1.586/1] rounded-xl overflow-hidden relative",
          !viewUrl || isLoadingIframe ? styles["card"] : null,
        )}
        style={(!viewUrl || isLoadingIframe) && customGradient ? { background: customGradient } : undefined}
      >
        {isGeneratingUrl || isLoadingIframe ? (
          <div className="w-full h-full flex items-center justify-center ">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-white/30 border-t-transparent"></div>
              <span className="text-white text-sm">{t("card_visual_loading")}</span>
            </div>
          </div>
        ) : null}

        {viewUrl ? (
          <iframe
            src={viewUrl}
            className={cn(
              "w-full h-full border-0 overflow-hidden!",
              styles["card-iframe"],
              isLoadingIframe ? "opacity-0" : "opacity-100",
            )}
            title={t("card_visual_view_title")}
            allow="payment; clipboard-write"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            scrolling="no"
            onLoad={() => setLoadedUrl(viewUrl)}
          />
        ) : null}

        {/* Placeholder hides once the iframe loads — otherwise the masked PAN
            would sit on top of the provider's real data. */}
        {(!viewUrl || isLoadingIframe) && secondaryLogoUrl && (
          <img
            src={secondaryLogoUrl}
            alt=""
            aria-hidden="true"
            className="absolute right-4 top-4 select-none object-contain"
            style={{
              width: `${secondaryLogoPx}px`,
              height: `${secondaryLogoPx}px`,
              transform: `translateY(${secondaryLogoTranslateY})`,
              pointerEvents: "none",
            }}
          />
        )}

        {(!viewUrl || isLoadingIframe) && (
          <div className="absolute inset-0 grid grid-rows-[auto_1fr_auto] gap-3 p-5">
            <div aria-hidden="true" />

            <span className="self-end font-[monospace] text-xl tracking-[0.15em] text-white [text-shadow:0_1px_rgba(0,0,0,0.4)]">
              {cardNumber}
            </span>

            <div className="grid grid-cols-[1fr_auto_auto] items-end gap-x-4 font-mono text-[15px] font-medium text-white">
              <div className="min-w-0 truncate font-sans">{holder}</div>
              <div className="text-right">Exp: {formattedExpiry}</div>
              <div className="text-right">CVV: ***</div>
            </div>
          </div>
        )}

        {/* State overlay last so it sits on top of the iframe. */}
        {disabled ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-destructive/20 backdrop-blur-[1px]">
            {/* DS has no --destructive-foreground; force text-white. */}
            <ColocaBadge
              variant="destructive"
              icon={Ban}
              className="!h-7 bg-destructive !px-3 !py-1 !text-sm text-white shadow-md [&_svg]:text-white"
            >
              {t("cards_status_disabled")}
            </ColocaBadge>
          </div>
        ) : frozen ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-sky-500/15 backdrop-blur-[1px]">
            <ColocaBadge
              variant="secondary"
              icon={Snowflake}
              className="!h-7 bg-sky-600 !px-3 !py-1 !text-sm text-white shadow-md [&_svg]:text-white"
            >
              {t("cards_status_frozen")}
            </ColocaBadge>
          </div>
        ) : null}
      </div>
    </div>
  );
};
